import { useState, type ReactNode } from 'react';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@clay-run/glaze/dropdown/index';
import { Icon } from '@clay-run/glaze/icon';
import { FLOW_ICONS } from '../../icons';
import { sfdcRecordUrl } from '../../links';
import { useDeferredMenuAction } from '../../shell-next/table/useDeferredMenuAction';
import { useMenuEscapeGuard } from './useMenuEscapeGuard';

/** The callbacks every node surface (table row menu, graph card menu) shares. */
export interface HierarchyNodeActions {
  onOpenAccount: (id: string) => void;
  onViewContacts: (id: string) => void;
  /** Opens the account's Opps tab (`?accountTab=opps`). */
  onViewDeals: (id: string) => void;
  onToggleChildren: (id: string) => void;
}

export interface HierarchyNodeFacts {
  accountId: string;
  isCurrent: boolean;
  hasChildren: boolean;
  /** The account has any deal (any status, regardless of filters): offers "View deals". */
  hasDeals?: boolean;
  childrenExpanded: boolean;
  actions: HierarchyNodeActions;
}

export interface HierarchyNodeActionItem {
  key: 'open' | 'contacts' | 'deals' | 'salesforce' | 'children';
  label: string;
  icon: (typeof FLOW_ICONS)[keyof typeof FLOW_ICONS];
  disabled: boolean;
  /** Route changes wait for the menu to close (focus return would race them); in-place actions do not. */
  navigates: boolean;
  run: () => void;
}

/**
 * The one list of node actions: Open account (disabled on the current account), View contacts,
 * View deals (only with deals), Open in Salesforce, Expand/Collapse children (only with children).
 * The row/card "..." menu and the full-screen "Selected account" card both render from it.
 */
export function nodeActionItems({ accountId, isCurrent, hasChildren, hasDeals = false, childrenExpanded, actions }: HierarchyNodeFacts): HierarchyNodeActionItem[] {
  const items: HierarchyNodeActionItem[] = [
    { key: 'open', label: 'Open account', icon: FLOW_ICONS.goTo, disabled: isCurrent, navigates: true, run: () => actions.onOpenAccount(accountId) },
    { key: 'contacts', label: 'View contacts', icon: FLOW_ICONS.people, disabled: false, navigates: true, run: () => actions.onViewContacts(accountId) },
  ];
  if (hasDeals) items.push({ key: 'deals', label: 'View deals', icon: FLOW_ICONS.opportunity, disabled: false, navigates: true, run: () => actions.onViewDeals(accountId) });
  items.push({
    key: 'salesforce', label: 'Open in Salesforce', icon: FLOW_ICONS.openExternal, disabled: false, navigates: false,
    run: () => { window.open(sfdcRecordUrl('Account', accountId), '_blank', 'noopener,noreferrer'); },
  });
  if (hasChildren) {
    items.push({
      key: 'children', label: childrenExpanded ? 'Collapse children' : 'Expand children',
      icon: childrenExpanded ? FLOW_ICONS.collapse : FLOW_ICONS.expanded, disabled: false, navigates: false,
      run: () => actions.onToggleChildren(accountId),
    });
  }
  return items;
}

export interface HierarchyNodeMenuProps extends HierarchyNodeFacts {
  accountName: string | null;
  /**
   * The Dropdown's trigger element. Defaults to a ghost "…" icon button (table
   * rows); the graph passes its card wrapped in `DropdownButtonUnstyled`.
   */
  trigger?: ReactNode;
}

/**
 * The node menu, rendered from `nodeActionItems`. Navigation items are deferred past the menu's
 * close (focus return would otherwise race the route change — same reasoning as `useDeferredMenuAction`).
 */
export function HierarchyNodeMenu({ accountName, trigger, ...facts }: HierarchyNodeMenuProps) {
  const { afterClose, handleOpenStateChange } = useDeferredMenuAction();
  const [open, setOpen] = useState(false);
  useMenuEscapeGuard(open);
  const { accountId } = facts;
  const label = accountName ?? accountId;
  const items = nodeActionItems(facts);
  return (
    <Dropdown placement="bottom-end" onOpenStateChange={isOpen => { setOpen(isOpen); handleOpenStateChange(isOpen); }}>
      {trigger ?? (
        <DropdownButton
          variant="ghost"
          size="sm"
          aria-label={`Actions for ${label}`}
          data-testid={`hierarchy-menu-trigger-${accountId}`}
          onClick={e => e.stopPropagation()}
        >
          <Icon type={FLOW_ICONS.more} size="sm" aria-hidden />
        </DropdownButton>
      )}
      <DropdownMenu>
        {items.map(item => (
          <DropdownItem
            key={item.key}
            disabled={item.disabled}
            onClick={e => { e.stopPropagation(); if (item.navigates) afterClose(item.run)(); else item.run(); }}
          >
            <Icon type={item.icon} size="sm" aria-hidden />
            {item.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}
